import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import db from '../Firebase';
import { collection, getDocs } from 'firebase/firestore/lite';

/* ─── Inline styles ─────────────────────────────────────────────────────── */
const G = {
    /* palette — light theme */
    bgDark: '#f0f2f7',       // page background
    sidebarBg: '#ffffff',       // sidebar white
    sidebarBorder: '#e4e7f0',
    surface: '#ffffff',       // card white
    surface2: '#f5f7fb',       // table header / inner bg
    accent: '#6c63ff',
    accentGlow: 'rgba(108,99,255,0.10)',
    accent2: '#00b894',
    textPrimary: '#1a1d2e',
    textSecondary: '#6b7280',
    textMuted: '#9ca3af',
    success: '#16a34a',
    warning: '#d97706',
    danger: '#dc2626',
    info: '#0284c7',
    border: '#e4e7f0',
};

const globalCss = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { height: 100%; background: ${G.bgDark}; color: ${G.textPrimary};
    font-family: 'DM Sans', sans-serif; overflow: hidden; }

  /* scrollbars */
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 2px; }

  /* nav item hover */
  .nav-item-link { transition: all .15s; }
  .nav-item-link:hover { color: ${G.accent} !important; background: ${G.accentGlow} !important; }
  .nav-item-link:hover .nav-dot { background: ${G.accent} !important; }
  .nav-item-link.active-link { color: ${G.accent} !important; background: ${G.accentGlow} !important; }
  .nav-item-link.active-link .nav-dot { background: ${G.accent} !important; }

  /* section header hover */
  .sec-header:hover .sec-label { color: ${G.textPrimary} !important; }

  /* stat card hover */
  .stat-card { transition: transform .2s, box-shadow .2s; }
  .stat-card:hover { transform: translateY(-2px); box-shadow: 0 10px 32px rgba(108,99,255,.12); }

  /* quick link hover */
  .ql-pill { transition: all .15s; }
  .ql-pill:hover { border-color: ${G.accent} !important; color: ${G.accent} !important;
    background: ${G.accentGlow} !important; }
  .ql-pill:hover .ql-dot-inner { background: ${G.accent} !important; }

  /* table row */
  .tbl-row { transition: background .15s; cursor: pointer; }
  .tbl-row:hover { background: ${G.surface2}; }

  /* logout */
  .logout-btn { transition: all .2s; }
  .logout-btn:hover { background: rgba(220,38,38,.12) !important; border-color: rgba(220,38,38,.4) !important; }

  /* collapse animation */
  .collapse-items {
    overflow: hidden;
    max-height: 0;
    transition: max-height .38s cubic-bezier(.4,0,.2,1);
  }
  .collapse-items.open { max-height: 2400px; }

  /* fade-up */
  @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  .fade-up { animation: fadeUp .35s ease both; }
  .fade-up-1{animation-delay:.05s} .fade-up-2{animation-delay:.1s}
  .fade-up-3{animation-delay:.15s} .fade-up-4{animation-delay:.2s}

  /* section arrow */
  .sec-arrow { transition: transform .25s, background .2s; }
  .sec-arrow.open { transform: rotate(90deg) !important; background: ${G.accentGlow} !important; color: ${G.accent} !important; }
`;

/* ─── Nav data ───────────────────────────────────────────────────────────── */
const NAV_SECTIONS = [
    {
        id: 'appdata', label: 'View App Data', emoji: '📊',
        items: [
            { label: 'Users Profile', to: '/users_profile' },
            { label: 'Portfolio', to: '/portfolio_data' },
            { label: 'Portfolio Analyser', to: '/portfolio_analyser' },
            { label: 'Portfolio Reviews', to: '/portfolio_reviews' },
            { label: 'Customer Data', to: '/customer_data' },
            { label: 'Referred Courses', to: '/referred_courses' },
            { label: "User's Location", to: '/location_data' },
            { label: 'Website Message', to: '/website_message' },
            { label: 'App Feedback', to: '/app_feedback' },
            { label: 'Appointment Analyser', to: '/appointment_analyser' },
            { label: 'My Business', to: '/my_business' },
            { label: 'Clicked Logs', to: '/clicked_logs' },
            { label: 'All Certificates', to: '/all_certificates' },
            { label: 'Workshop Feedback', to: '/workshop_feedback' },
            { label: 'Workshops Payment', to: '/workshop_payment' },
            { label: 'Expense List', to: '/expenses_list' },
            { label: 'Registration Count', to: '/workshop_registration_count' },
            { label: 'App Referral', to: '/app_referral' },
            { label: 'Feedback Reply', to: '/feedback_reply' },
            { label: 'Users Checkin', to: '/user_last_checkin' },
            { label: 'Login Logs', to: '/login_logs' },
            { label: 'Downloaded Cert.', to: '/downloaded_certificate' },
            { label: 'Topic Request', to: '/topic_request' },
        ],
    },
    {
        id: 'requests', label: 'View All Requests', emoji: '📨',
        items: [
            { label: 'Beauty Kit Requests', to: '/beauty_kit_requests' },
            { label: 'Upcoming Session', to: '/upcoming_session' },
            { label: 'Book Demo Requests', to: '/book_demo_requests' },
            { label: 'Hire Requests', to: '/hire_request' },
            { label: 'Event Requests', to: '/event_request' },
            { label: 'Loan Requests', to: '/loan_request' },
            { label: 'Loan Remarks', to: '/loan_remark' },
            { label: 'Workshop Registration', to: '/workshop_registration' },
            { label: 'Workshop Payment', to: '/workshop_payment' },
            { label: 'All Workshop', to: '/workshop' },
            { label: 'Workshop Query', to: '/workshop_query' },
            { label: 'Workshop Event Request', to: '/workshop_event_request' },
            { label: 'Delete Portfolio Req.', to: '/delete_portfolio_req' },
            { label: 'Advertise Feedback', to: '/advertise_feedback' },
        ],
    },
    {
        id: 'create', label: 'Create', emoji: '✏️',
        items: [
            { label: 'Guidance Session', to: '/guidance_session' },
            { label: 'Workshop', to: '/workshop_data' },
            { label: 'Workshop Hindi', to: '/workshop_data_hindi' },
            { label: 'Workshop Reminder', to: '/workshop_reminder' },
            { label: 'Pin Workshop', to: '/pin_workshop' },
            { label: 'Workshop Attendance', to: '/workshop_attendance' },
            { label: 'Workshop Glimpses', to: '/workshop_glimpses' },
            { label: 'Attendance Status', to: '/workshop_attendance_status' },
            { label: 'Upload Recorded Session', to: '/upload_recorded_video' },
            { label: 'Upload Certificate', to: '/upload_certificates' },
            { label: 'Send Whatsapp', to: '/send_whatsapp' },
            { label: 'Create Whatsapp Template', to: '/create_whatsapp_template' },
            { label: 'Courses', to: '/course_data' },
            { label: 'Course Hindi', to: '/course_data_hindi' },
            { label: 'Add Slider Images', to: '/add_slider_images' },
            { label: 'Add Workshop Slider', to: '/workshop_slider_image' },
            { label: 'Add Global Notification', to: '/global_notification' },
            { label: 'Advertise Download', to: '/advertise_download' },
            { label: 'After Session Slider', to: '/session_slider_images' },
            { label: 'Workshop Code', to: '/workshop_code' },
            { label: 'Workshop Tag', to: '/workshop_tag' },
            { label: 'Add Adv Slider', to: '/advertise_slider' },
        ],
    },
    {
        id: 'new', label: 'New', emoji: '🆕',
        items: [
            { label: 'Upload Course', to: '/CourseUploadForm' },
            { label: 'Add Quiz', to: '/add_quiz' },
            { label: 'Add Expert', to: '/add_expert' },
            { label: 'Add Enrollment', to: '/custom_enrollment' },
            { label: 'View Course Enrollment', to: '/course_enrollment' },
            { label: 'Membership Data', to: '/membership_tracking' },
            { label: 'Booking', to: '/bookings' },
            { label: 'Add Doubt Sessions', to: '/add_doubt_session' },
            { label: 'Doubt Sessions Data', to: '/doubt_session' },
            { label: 'Community', to: '/community' },
            { label: 'App Queries', to: '/query_data' },
            { label: 'Faqs Query', to: '/faqs_query' },
            { label: 'Profile Type', to: '/profile_type' },
            { label: 'Course Video Update', to: '/course_video_update' },
            { label: 'Course Attendance Update', to: '/course_attendance_update' },
            { label: 'Course Timestamp Update', to: '/course_timestamp_update' },
            { label: 'Booked Appointments', to: '/appointment_booked' },
            { label: 'Appointment Analyser', to: '/appointment_analyser' },
            { label: 'Finance Query', to: '/finance_query' },
            { label: 'Certificate Download', to: '/certificate_downloads' },
            { label: 'Course Report', to: '/users_course_data' },
            { label: 'Analytics Preview', to: '/analytics_preview' },
            { label: 'Log Analytics', to: '/dashbaordall' },
            { label: 'Homepage Banner', to: '/apptopbanner' },
            { label: 'Homepage Poster', to: '/homepage_poster' },
            { label: 'Transaction History', to: '/transaction_history' },
            { label: 'Requested Topics', to: '/requested_topics' },
            { label: 'Know More About Course', to: '/know_more_about_course' },
            { label: 'Active Members', to: '/active_membership' },
            { label: 'Certificate Data', to: '/downloaded_certificate_data' },
            { label: 'Workshop Registration', to: '/workshop_registrations' },
            { label: 'Udyam Registration', to: '/UdyamRegistrationData' },
            { label: 'Ecommerce Support Data', to: '/EcommerceSupportData' },
            { label: 'Edit Course', to: '/editcoursepage' },
            { label: 'Hindi Workshop Name', to: '/workshophindiversion' },
        ],
    },
    {
        id: 'grow', label: 'Grow', emoji: '🌱',
        items: [
            { label: 'Call Back Request', to: '/call_back_request' },
            { label: 'Grow Data', to: '/grow_all_data' },
            { label: 'Ready Made Delete', to: '/readymadepage' },
            { label: 'DIY Delete', to: '/diycardpage' },
            { label: 'Studio Requests', to: '/orders_data' },
        ],
    },
];

const RECENT_REQUESTS = [
    { user: 'Priya Sharma', type: 'Book Demo', date: 'Today, 10:24 AM', status: 'Pending', statusClass: 'yellow' },
    { user: 'Rohan Mehta', type: 'Workshop Registration', date: 'Today, 9:12 AM', status: 'Confirmed', statusClass: 'green' },
    { user: 'Anita Joshi', type: 'Hire Request', date: 'Yesterday, 5:40 PM', status: 'In Review', statusClass: 'blue' },
    { user: 'Vikram Das', type: 'Loan Request', date: 'Yesterday, 2:10 PM', status: 'Rejected', statusClass: 'red' },
    { user: 'Sunita Kapoor', type: 'Beauty Kit', date: 'May 3, 11:00 AM', status: 'Fulfilled', statusClass: 'green' },
];

const BADGE_COLORS = {
    green: { bg: 'rgba(34,197,94,.12)', color: '#22c55e' },
    yellow: { bg: 'rgba(245,158,11,.12)', color: '#f59e0b' },
    blue: { bg: 'rgba(56,189,248,.1)', color: '#38bdf8' },
    red: { bg: 'rgba(239,68,68,.1)', color: '#ef4444' },
};

/* ─── Sub-components ─────────────────────────────────────────────────────── */

function NavSection({ section, activeLink, setActiveLink }) {
    const [open, setOpen] = useState(false);

    return (
        <div>
            {/* Section header */}
            <div
                className="sec-header"
                onClick={() => setOpen(o => !o)}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 20px 8px', cursor: 'pointer', userSelect: 'none',
                }}
            >
                <span
                    className="sec-label"
                    style={{
                        fontSize: 10.5, fontWeight: 700, letterSpacing: '.12em',
                        textTransform: 'uppercase', color: G.textMuted, transition: 'color .2s',
                    }}
                >
                    {section.emoji}&nbsp; {section.label}
                </span>
                <span
                    className={`sec-arrow${open ? ' open' : ''}`}
                    style={{
                        width: 18, height: 18,
                        background: G.surface2,
                        borderRadius: 5,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, color: G.textMuted,
                    }}
                >
                    ›
                </span>
            </div>

            {/* Collapsible items */}
            <div className={`collapse-items${open ? ' open' : ''}`}>
                {section.items.map(item => (
                    <Link
                        key={item.to}
                        to={item.to}
                        className={`nav-item-link${activeLink === item.to ? ' active-link' : ''}`}
                        onClick={() => setActiveLink(item.to)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '7px 20px 7px 30px',
                            color: G.textSecondary,
                            fontSize: 13, fontWeight: 400,
                            textDecoration: 'none',
                            position: 'relative',
                        }}
                    >
                        <span
                            className="nav-dot"
                            style={{
                                width: 5, height: 5, borderRadius: '50%',
                                background: G.textMuted, flexShrink: 0, transition: 'background .2s',
                            }}
                        />
                        {item.label}
                    </Link>
                ))}
            </div>
        </div>
    );
}

function StatCard({ icon, value, label, change, changeUp, colorClass, delay }) {
    const topColors = {
        purple: 'linear-gradient(90deg,#6c63ff,#a78bfa)',
        teal: 'linear-gradient(90deg,#00d4aa,#34d399)',
        amber: 'linear-gradient(90deg,#f59e0b,#fbbf24)',
        sky: 'linear-gradient(90deg,#38bdf8,#7dd3fc)',
    };
    const iconBgs = {
        purple: 'rgba(108,99,255,.1)',
        teal: 'rgba(0,184,148,.12)',
        amber: 'rgba(217,119,6,.1)',
        sky: 'rgba(2,132,199,.1)',
    };

    return (
        <div
            className={`stat-card fade-up fade-up-${delay}`}
            style={{
                background: G.surface,
                border: `1px solid ${G.border}`,
                borderRadius: 14,
                padding: 20,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* top accent line */}
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 2,
                background: topColors[colorClass],
            }} />
            <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: iconBgs[colorClass],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, marginBottom: 14,
            }}>
                {icon}
            </div>
            <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 28, fontWeight: 700, lineHeight: 1 }}>
                {value}
            </div>
            <div style={{ fontSize: 12.5, color: G.textSecondary, marginTop: 6, fontWeight: 500 }}>
                {label}
            </div>
            <div style={{
                fontSize: 11.5, marginTop: 10, display: 'flex', alignItems: 'center', gap: 4,
                color: changeUp ? G.success : G.danger,
            }}>
                {changeUp ? '↑' : '↓'} {change}
            </div>
        </div>
    );
}

function QuickSection({ title, badge, items }) {
    return (
        <div style={{
            background: G.surface, border: `1px solid ${G.border}`,
            borderRadius: 14, padding: 20,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
                <div style={{
                    fontSize: 11, padding: '3px 8px', borderRadius: 6,
                    background: G.surface2, color: G.textSecondary,
                }}>
                    {badge}
                </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {items.map(item => (
                    <div
                        key={item}
                        className="ql-pill"
                        style={{
                            padding: '6px 12px',
                            background: G.surface2,
                            border: `1px solid ${G.border}`,
                            borderRadius: 8,
                            fontSize: 12,
                            color: G.textSecondary,
                            cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        <span className="ql-dot-inner" style={{
                            width: 5, height: 5, borderRadius: '50%',
                            background: G.textMuted, flexShrink: 0,
                        }} />
                        {item}
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */

function AdminDashboard() {
    const navigate = useNavigate();
    const [totalCount, setTotalCount] = useState('—');
    const [activeLink, setActiveLink] = useState('');

    /* inject global CSS once */
    useEffect(() => {
        const id = 'admin-panel-styles';
        if (!document.getElementById(id)) {
            const style = document.createElement('style');
            style.id = id;
            style.textContent = globalCss;
            document.head.appendChild(style);
        }
        return () => {
            const el = document.getElementById(id);
            if (el) el.remove();
        };
    }, []);

    /* auth guard */
    useEffect(() => {
        const email = localStorage.getItem('email');
        const password = localStorage.getItem('password');
        const remember = localStorage.getItem('remember');
        if (remember === 'false' || !email || !password) navigate('/signin');
    }, [navigate]);

    /* fetch total count */
    useEffect(() => {
        const fetchData = async () => {
            try {
                const snapshot = await getDocs(collection(db, 'personal_details'));
                setTotalCount(snapshot.size.toLocaleString('en-IN'));
            } catch (err) {
                console.error('Error fetching data:', err);
            }
        };
        fetchData();
    }, []);

    const logout = () => {
        localStorage.removeItem('email');
        localStorage.removeItem('password');
        localStorage.removeItem('remember');
        navigate('/signin');
    };

    return (
        <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: G.bgDark }}>

            {/* ── SIDEBAR ── */}
            <aside style={{
                width: 260, minWidth: 260,
                background: G.sidebarBg,
                borderRight: `1px solid ${G.sidebarBorder}`,
                boxShadow: '2px 0 12px rgba(108,99,255,.06)',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>

                {/* Brand */}
                <div style={{
                    padding: '20px 20px 16px',
                    display: 'flex', alignItems: 'center', gap: 12,
                    borderBottom: `1px solid ${G.border}`, flexShrink: 0,
                }}>
                    <div style={{
                        width: 36, height: 36,
                        background: 'linear-gradient(135deg,#6c63ff,#a78bfa)',
                        borderRadius: 10,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, flexShrink: 0,
                    }}>
                        ⚡
                    </div>
                    <div>
                        <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, fontWeight: 700 }}>
                            AdminDashboard
                        </div>
                        <div style={{ fontSize: 11, color: G.textSecondary }}>Management Console</div>
                    </div>
                </div>

                {/* Nav scroll */}
                <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '10px 0 16px' }}>
                    {NAV_SECTIONS.map(sec => (
                        <NavSection
                            key={sec.id}
                            section={sec}
                            activeLink={activeLink}
                            setActiveLink={setActiveLink}
                        />
                    ))}

                    {/* Logout */}
                    <div style={{ padding: '12px 16px 0' }}>
                        <button
                            className="logout-btn"
                            onClick={logout}
                            style={{
                                width: '100%', padding: '10px 16px',
                                display: 'flex', alignItems: 'center', gap: 10,
                                background: 'rgba(239,68,68,.08)',
                                border: '1px solid rgba(239,68,68,.18)',
                                borderRadius: 10,
                                color: G.danger,
                                fontSize: 13, fontWeight: 500,
                                cursor: 'pointer',
                            }}
                        >
                            <span>⎋</span> Logout
                        </button>
                    </div>
                </nav>

                {/* Footer user row */}
                <div style={{ padding: '14px 16px', borderTop: `1px solid ${G.border}`, flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                            width: 32, height: 32,
                            background: 'linear-gradient(135deg,#6c63ff,#00d4aa)',
                            borderRadius: 9,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0,
                        }}>A</div>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>Admin</div>
                            <div style={{ fontSize: 11, color: G.textMuted }}>Super Admin</div>
                        </div>
                        <div style={{
                            marginLeft: 'auto',
                            width: 7, height: 7, borderRadius: '50%',
                            background: G.success,
                            boxShadow: `0 0 6px ${G.success}`,
                        }} />
                    </div>
                </div>
            </aside>

            {/* ── MAIN ── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                {/* Topbar */}
                <header style={{
                    height: 60,
                    background: G.surface,
                    borderBottom: `1px solid ${G.border}`,
                    display: 'flex', alignItems: 'center',
                    padding: '0 28px', gap: 16, flexShrink: 0,
                }}>
                    <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 16, fontWeight: 600, flex: 1 }}>
                        Dashboard Overview
                    </div>
                    <div style={{
                        background: G.accentGlow, color: G.accent,
                        padding: '4px 12px', borderRadius: 20,
                        fontSize: 12, fontWeight: 500,
                        border: '1px solid rgba(108,99,255,.25)',
                    }}>
                        ● Live
                    </div>
                    <div style={{ fontSize: 12, color: G.textSecondary }}>
                        {new Date().toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                    </div>
                    <div style={{
                        width: 34, height: 34,
                        background: 'linear-gradient(135deg,#6c63ff,#00d4aa)',
                        borderRadius: 10,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer',
                    }}>A</div>
                </header>

                {/* Scrollable content */}
                <main style={{ flex: 1, overflowY: 'auto', padding: 28 }}>

                    {/* Page heading */}
                    <div style={{ marginBottom: 28 }}>
                        <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 24, fontWeight: 700 }}>
                            Welcome back, Admin 👋
                        </h1>
                        <p style={{ color: G.textSecondary, fontSize: 13.5, marginTop: 4 }}>
                            Here's what's happening with your platform today.
                        </p>
                    </div>

                    {/* Stat cards */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: 16, marginBottom: 28,
                    }}>
                        <StatCard icon="📱" value={totalCount} label="App Downloads" change="12% from last month" changeUp delay={1} colorClass="purple" />
                        <StatCard icon="🎓" value="148" label="Workshop Registrations" change="8% this week" changeUp delay={2} colorClass="teal" />
                        <StatCard icon="📋" value="34" label="Pending Requests" change="5 new today" changeUp delay={3} colorClass="amber" />
                        <StatCard icon="🏆" value="209" label="Certificates Issued" change="3% this month" changeUp delay={4} colorClass="sky" />
                    </div>

                    {/* Quick section cards */}
                    <div style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: 20, marginBottom: 28,
                    }}>
                        <QuickSection title="📊 App Data" badge={`${NAV_SECTIONS[0].items.length} sections`}
                            items={NAV_SECTIONS[0].items.slice(0, 8).map(i => i.label)} />
                        <QuickSection title="📨 Requests" badge={`${NAV_SECTIONS[1].items.length} sections`}
                            items={NAV_SECTIONS[1].items.slice(0, 8).map(i => i.label)} />
                        <QuickSection title="✏️ Create" badge={`${NAV_SECTIONS[2].items.length} sections`}
                            items={NAV_SECTIONS[2].items.slice(0, 8).map(i => i.label)} />
                        <QuickSection title="🆕 New / Grow" badge={`${NAV_SECTIONS[3].items.length + NAV_SECTIONS[4].items.length} sections`}
                            items={[...NAV_SECTIONS[3].items, ...NAV_SECTIONS[4].items].slice(0, 8).map(i => i.label)} />
                    </div>

                    {/* Recent requests table */}
                    <div style={{
                        background: G.surface, border: `1px solid ${G.border}`,
                        borderRadius: 14, overflow: 'hidden',
                    }}>
                        <div style={{
                            padding: '16px 20px',
                            borderBottom: `1px solid ${G.border}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <div style={{ fontSize: 14, fontWeight: 600 }}>Recent Requests</div>
                            <div style={{ fontSize: 12, color: G.accent, cursor: 'pointer' }}>View all →</div>
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: G.surface2, borderBottom: `1px solid ${G.border}` }}>
                                    {['User', 'Request Type', 'Date', 'Status'].map(h => (
                                        <th key={h} style={{
                                            padding: '10px 20px', textAlign: 'left',
                                            fontSize: 11, fontWeight: 600, letterSpacing: '.07em',
                                            textTransform: 'uppercase', color: G.textMuted,
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {RECENT_REQUESTS.map((row, i) => {
                                    const bc = BADGE_COLORS[row.statusClass];
                                    return (
                                        <tr
                                            key={i}
                                            className="tbl-row"
                                            style={{ borderBottom: i < RECENT_REQUESTS.length - 1 ? `1px solid ${G.border}` : 'none' }}
                                        >
                                            <td style={{ padding: '12px 20px', fontSize: 13, color: G.textPrimary, fontWeight: 500 }}>{row.user}</td>
                                            <td style={{ padding: '12px 20px', fontSize: 13, color: G.textSecondary }}>{row.type}</td>
                                            <td style={{ padding: '12px 20px', fontSize: 13, color: G.textSecondary }}>{row.date}</td>
                                            <td style={{ padding: '12px 20px' }}>
                                                <span style={{
                                                    display: 'inline-block', padding: '3px 9px', borderRadius: 6,
                                                    fontSize: 11, fontWeight: 600,
                                                    background: bc.bg, color: bc.color,
                                                }}>{row.status}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* bottom spacer */}
                    <div style={{ height: 24 }} />
                </main>
            </div>
        </div>
    );
}

export default AdminDashboard;



// import React, { useState, useEffect } from 'react';
// import { useNavigate, Link } from 'react-router-dom';
// import db from './Firebase';
// import { collection, getDocs } from 'firebase/firestore/lite';

// /* ─── Inline styles ─────────────────────────────────────────────────────── */
// const G = {
//   /* palette */
//   bgDark:      '#0d0f14',
//   sidebarBg:   '#111318',
//   sidebarBorder:'#1e2130',
//   surface:     '#16191f',
//   surface2:    '#1c2030',
//   accent:      '#6c63ff',
//   accentGlow:  'rgba(108,99,255,0.15)',
//   accent2:     '#00d4aa',
//   textPrimary: '#e8eaf0',
//   textSecondary:'#7a8099',
//   textMuted:   '#454b62',
//   success:     '#22c55e',
//   warning:     '#f59e0b',
//   danger:      '#ef4444',
//   info:        '#38bdf8',
//   border:      '#1e2232',
// };

// const globalCss = `
//   @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&display=swap');

//   *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
//   html, body { height: 100%; background: ${G.bgDark}; color: ${G.textPrimary};
//     font-family: 'DM Sans', sans-serif; overflow: hidden; }

//   /* scrollbars */
//   ::-webkit-scrollbar { width: 4px; }
//   ::-webkit-scrollbar-track { background: transparent; }
//   ::-webkit-scrollbar-thumb { background: ${G.border}; border-radius: 2px; }

//   /* nav item hover */
//   .nav-item-link { transition: all .15s; }
//   .nav-item-link:hover { color: ${G.textPrimary} !important; background: rgba(255,255,255,.03) !important; }
//   .nav-item-link:hover .nav-dot { background: ${G.accent} !important; }
//   .nav-item-link.active-link { color: ${G.accent} !important; background: ${G.accentGlow} !important; }
//   .nav-item-link.active-link .nav-dot { background: ${G.accent} !important; }

//   /* section header hover */
//   .sec-header:hover .sec-label { color: ${G.textSecondary} !important; }

//   /* stat card hover */
//   .stat-card { transition: transform .2s, box-shadow .2s; }
//   .stat-card:hover { transform: translateY(-2px); box-shadow: 0 10px 32px rgba(0,0,0,.35); }

//   /* quick link hover */
//   .ql-pill { transition: all .15s; }
//   .ql-pill:hover { border-color: ${G.accent} !important; color: ${G.accent} !important;
//     background: ${G.accentGlow} !important; }
//   .ql-pill:hover .ql-dot-inner { background: ${G.accent} !important; }

//   /* table row */
//   .tbl-row { transition: background .15s; cursor: pointer; }
//   .tbl-row:hover { background: rgba(255,255,255,.025); }

//   /* logout */
//   .logout-btn { transition: all .2s; }
//   .logout-btn:hover { background: rgba(239,68,68,.18) !important; border-color: rgba(239,68,68,.45) !important; }

//   /* collapse animation */
//   .collapse-items {
//     overflow: hidden;
//     max-height: 0;
//     transition: max-height .38s cubic-bezier(.4,0,.2,1);
//   }
//   .collapse-items.open { max-height: 2400px; }

//   /* fade-up */
//   @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
//   .fade-up { animation: fadeUp .35s ease both; }
//   .fade-up-1{animation-delay:.05s} .fade-up-2{animation-delay:.1s}
//   .fade-up-3{animation-delay:.15s} .fade-up-4{animation-delay:.2s}

//   /* section arrow */
//   .sec-arrow { transition: transform .25s, background .2s; }
//   .sec-arrow.open { transform: rotate(90deg) !important; background: ${G.accentGlow} !important; color: ${G.accent} !important; }
// `;

// /* ─── Nav data ───────────────────────────────────────────────────────────── */
// const NAV_SECTIONS = [
//   {
//     id: 'appdata', label: 'View App Data', emoji: '📊',
//     items: [
//       { label: 'Users Profile',        to: '/users_profile' },
//       { label: 'Portfolio',            to: '/portfolio_data' },
//       { label: 'Portfolio Analyser',   to: '/portfolio_analyser' },
//       { label: 'Portfolio Reviews',    to: '/portfolio_reviews' },
//       { label: 'Customer Data',        to: '/customer_data' },
//       { label: 'Referred Courses',     to: '/referred_courses' },
//       { label: "User's Location",      to: '/location_data' },
//       { label: 'Website Message',      to: '/website_message' },
//       { label: 'App Feedback',         to: '/app_feedback' },
//       { label: 'Appointment Analyser', to: '/appointment_analyser' },
//       { label: 'My Business',          to: '/my_business' },
//       { label: 'Clicked Logs',         to: '/clicked_logs' },
//       { label: 'All Certificates',     to: '/all_certificates' },
//       { label: 'Workshop Feedback',    to: '/workshop_feedback' },
//       { label: 'Workshops Payment',    to: '/workshop_payment' },
//       { label: 'Expense List',         to: '/expenses_list' },
//       { label: 'Registration Count',   to: '/workshop_registration_count' },
//       { label: 'App Referral',         to: '/app_referral' },
//       { label: 'Feedback Reply',       to: '/feedback_reply' },
//       { label: 'Users Checkin',        to: '/user_last_checkin' },
//       { label: 'Login Logs',           to: '/login_logs' },
//       { label: 'Downloaded Cert.',     to: '/downloaded_certificate' },
//       { label: 'Topic Request',        to: '/topic_request' },
//     ],
//   },
//   {
//     id: 'requests', label: 'View All Requests', emoji: '📨',
//     items: [
//       { label: 'Beauty Kit Requests',      to: '/beauty_kit_requests' },
//       { label: 'Upcoming Session',         to: '/upcoming_session' },
//       { label: 'Book Demo Requests',       to: '/book_demo_requests' },
//       { label: 'Hire Requests',            to: '/hire_request' },
//       { label: 'Event Requests',           to: '/event_request' },
//       { label: 'Loan Requests',            to: '/loan_request' },
//       { label: 'Loan Remarks',             to: '/loan_remark' },
//       { label: 'Workshop Registration',    to: '/workshop_registration' },
//       { label: 'Workshop Payment',         to: '/workshop_payment' },
//       { label: 'All Workshop',             to: '/workshop' },
//       { label: 'Workshop Query',           to: '/workshop_query' },
//       { label: 'Workshop Event Request',   to: '/workshop_event_request' },
//       { label: 'Delete Portfolio Req.',    to: '/delete_portfolio_req' },
//       { label: 'Advertise Feedback',       to: '/advertise_feedback' },
//     ],
//   },
//   {
//     id: 'create', label: 'Create', emoji: '✏️',
//     items: [
//       { label: 'Guidance Session',         to: '/guidance_session' },
//       { label: 'Workshop',                 to: '/workshop_data' },
//       { label: 'Workshop Hindi',           to: '/workshop_data_hindi' },
//       { label: 'Workshop Reminder',        to: '/workshop_reminder' },
//       { label: 'Pin Workshop',             to: '/pin_workshop' },
//       { label: 'Workshop Attendance',      to: '/workshop_attendance' },
//       { label: 'Workshop Glimpses',        to: '/workshop_glimpses' },
//       { label: 'Attendance Status',        to: '/workshop_attendance_status' },
//       { label: 'Upload Recorded Session',  to: '/upload_recorded_video' },
//       { label: 'Upload Certificate',       to: '/upload_certificates' },
//       { label: 'Send Whatsapp',            to: '/send_whatsapp' },
//       { label: 'Create Whatsapp Template', to: '/create_whatsapp_template' },
//       { label: 'Courses',                  to: '/course_data' },
//       { label: 'Course Hindi',             to: '/course_data_hindi' },
//       { label: 'Add Slider Images',        to: '/add_slider_images' },
//       { label: 'Add Workshop Slider',      to: '/workshop_slider_image' },
//       { label: 'Add Global Notification',  to: '/global_notification' },
//       { label: 'Advertise Download',       to: '/advertise_download' },
//       { label: 'After Session Slider',     to: '/session_slider_images' },
//       { label: 'Workshop Code',            to: '/workshop_code' },
//       { label: 'Workshop Tag',             to: '/workshop_tag' },
//       { label: 'Add Adv Slider',           to: '/advertise_slider' },
//     ],
//   },
//   {
//     id: 'new', label: 'New', emoji: '🆕',
//     items: [
//       { label: 'Upload Course',            to: '/CourseUploadForm' },
//       { label: 'Add Quiz',                 to: '/add_quiz' },
//       { label: 'Add Expert',               to: '/add_expert' },
//       { label: 'Add Enrollment',           to: '/custom_enrollment' },
//       { label: 'View Course Enrollment',   to: '/course_enrollment' },
//       { label: 'Membership Data',          to: '/membership_tracking' },
//       { label: 'Booking',                  to: '/bookings' },
//       { label: 'Add Doubt Sessions',       to: '/add_doubt_session' },
//       { label: 'Doubt Sessions Data',      to: '/doubt_session' },
//       { label: 'Community',                to: '/community' },
//       { label: 'App Queries',              to: '/query_data' },
//       { label: 'Faqs Query',               to: '/faqs_query' },
//       { label: 'Profile Type',             to: '/profile_type' },
//       { label: 'Course Video Update',      to: '/course_video_update' },
//       { label: 'Course Attendance Update', to: '/course_attendance_update' },
//       { label: 'Course Timestamp Update',  to: '/course_timestamp_update' },
//       { label: 'Booked Appointments',      to: '/appointment_booked' },
//       { label: 'Appointment Analyser',     to: '/appointment_analyser' },
//       { label: 'Finance Query',            to: '/finance_query' },
//       { label: 'Certificate Download',     to: '/certificate_downloads' },
//       { label: 'Course Report',            to: '/users_course_data' },
//       { label: 'Analytics Preview',        to: '/analytics_preview' },
//       { label: 'Log Analytics',            to: '/dashbaordall' },
//       { label: 'Homepage Banner',          to: '/apptopbanner' },
//       { label: 'Homepage Poster',          to: '/homepage_poster' },
//       { label: 'Transaction History',      to: '/transaction_history' },
//       { label: 'Requested Topics',         to: '/requested_topics' },
//       { label: 'Know More About Course',   to: '/know_more_about_course' },
//       { label: 'Active Members',           to: '/active_membership' },
//       { label: 'Certificate Data',         to: '/downloaded_certificate_data' },
//       { label: 'Workshop Registration',    to: '/workshop_registrations' },
//       { label: 'Udyam Registration',       to: '/UdyamRegistrationData' },
//       { label: 'Ecommerce Support Data',   to: '/EcommerceSupportData' },
//       { label: 'Edit Course',              to: '/editcoursepage' },
//       { label: 'Hindi Workshop Name',      to: '/workshophindiversion' },
//     ],
//   },
//   {
//     id: 'grow', label: 'Grow', emoji: '🌱',
//     items: [
//       { label: 'Call Back Request', to: '/call_back_request' },
//       { label: 'Grow Data',         to: '/grow_all_data' },
//       { label: 'Ready Made Delete', to: '/readymadepage' },
//       { label: 'DIY Delete',        to: '/diycardpage' },
//       { label: 'Studio Requests',   to: '/orders_data' },
//     ],
//   },
// ];

// const RECENT_REQUESTS = [
//   { user: 'Priya Sharma',   type: 'Book Demo',             date: 'Today, 10:24 AM',      status: 'Pending',   statusClass: 'yellow' },
//   { user: 'Rohan Mehta',    type: 'Workshop Registration', date: 'Today, 9:12 AM',       status: 'Confirmed', statusClass: 'green'  },
//   { user: 'Anita Joshi',    type: 'Hire Request',          date: 'Yesterday, 5:40 PM',   status: 'In Review', statusClass: 'blue'   },
//   { user: 'Vikram Das',     type: 'Loan Request',          date: 'Yesterday, 2:10 PM',   status: 'Rejected',  statusClass: 'red'    },
//   { user: 'Sunita Kapoor',  type: 'Beauty Kit',            date: 'May 3, 11:00 AM',      status: 'Fulfilled', statusClass: 'green'  },
// ];

// const BADGE_COLORS = {
//   green:  { bg: 'rgba(34,197,94,.12)',  color: '#22c55e' },
//   yellow: { bg: 'rgba(245,158,11,.12)', color: '#f59e0b' },
//   blue:   { bg: 'rgba(56,189,248,.1)',  color: '#38bdf8' },
//   red:    { bg: 'rgba(239,68,68,.1)',   color: '#ef4444' },
// };

// /* ─── Sub-components ─────────────────────────────────────────────────────── */

// function NavSection({ section, activeLink, setActiveLink }) {
//   const [open, setOpen] = useState(false);

//   return (
//     <div>
//       {/* Section header */}
//       <div
//         className="sec-header"
//         onClick={() => setOpen(o => !o)}
//         style={{
//           display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//           padding: '16px 20px 8px', cursor: 'pointer', userSelect: 'none',
//         }}
//       >
//         <span
//           className="sec-label"
//           style={{
//             fontSize: 10.5, fontWeight: 700, letterSpacing: '.12em',
//             textTransform: 'uppercase', color: G.textMuted, transition: 'color .2s',
//           }}
//         >
//           {section.emoji}&nbsp; {section.label}
//         </span>
//         <span
//           className={`sec-arrow${open ? ' open' : ''}`}
//           style={{
//             width: 18, height: 18,
//             background: G.surface2,
//             borderRadius: 5,
//             display: 'flex', alignItems: 'center', justifyContent: 'center',
//             fontSize: 12, color: G.textMuted,
//           }}
//         >
//           ›
//         </span>
//       </div>

//       {/* Collapsible items */}
//       <div className={`collapse-items${open ? ' open' : ''}`}>
//         {section.items.map(item => (
//           <Link
//             key={item.to}
//             to={item.to}
//             className={`nav-item-link${activeLink === item.to ? ' active-link' : ''}`}
//             onClick={() => setActiveLink(item.to)}
//             style={{
//               display: 'flex', alignItems: 'center', gap: 10,
//               padding: '7px 20px 7px 30px',
//               color: G.textSecondary,
//               fontSize: 13, fontWeight: 400,
//               textDecoration: 'none',
//               position: 'relative',
//             }}
//           >
//             <span
//               className="nav-dot"
//               style={{
//                 width: 5, height: 5, borderRadius: '50%',
//                 background: G.textMuted, flexShrink: 0, transition: 'background .2s',
//               }}
//             />
//             {item.label}
//           </Link>
//         ))}
//       </div>
//     </div>
//   );
// }

// function StatCard({ icon, value, label, change, changeUp, colorClass, delay }) {
//   const topColors = {
//     purple: 'linear-gradient(90deg,#6c63ff,#a78bfa)',
//     teal:   'linear-gradient(90deg,#00d4aa,#34d399)',
//     amber:  'linear-gradient(90deg,#f59e0b,#fbbf24)',
//     sky:    'linear-gradient(90deg,#38bdf8,#7dd3fc)',
//   };
//   const iconBgs = {
//     purple: G.accentGlow,
//     teal:   'rgba(0,212,170,.12)',
//     amber:  'rgba(245,158,11,.12)',
//     sky:    'rgba(56,189,248,.1)',
//   };

//   return (
//     <div
//       className={`stat-card fade-up fade-up-${delay}`}
//       style={{
//         background: G.surface,
//         border: `1px solid ${G.border}`,
//         borderRadius: 14,
//         padding: 20,
//         position: 'relative',
//         overflow: 'hidden',
//       }}
//     >
//       {/* top accent line */}
//       <div style={{
//         position: 'absolute', top: 0, left: 0, right: 0, height: 2,
//         background: topColors[colorClass],
//       }} />
//       <div style={{
//         width: 40, height: 40, borderRadius: 10,
//         background: iconBgs[colorClass],
//         display: 'flex', alignItems: 'center', justifyContent: 'center',
//         fontSize: 18, marginBottom: 14,
//       }}>
//         {icon}
//       </div>
//       <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 28, fontWeight: 700, lineHeight: 1 }}>
//         {value}
//       </div>
//       <div style={{ fontSize: 12.5, color: G.textSecondary, marginTop: 6, fontWeight: 500 }}>
//         {label}
//       </div>
//       <div style={{
//         fontSize: 11.5, marginTop: 10, display: 'flex', alignItems: 'center', gap: 4,
//         color: changeUp ? G.success : G.danger,
//       }}>
//         {changeUp ? '↑' : '↓'} {change}
//       </div>
//     </div>
//   );
// }

// function QuickSection({ title, badge, items }) {
//   return (
//     <div style={{
//       background: G.surface, border: `1px solid ${G.border}`,
//       borderRadius: 14, padding: 20,
//     }}>
//       <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
//         <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
//         <div style={{
//           fontSize: 11, padding: '3px 8px', borderRadius: 6,
//           background: G.surface2, color: G.textSecondary,
//         }}>
//           {badge}
//         </div>
//       </div>
//       <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
//         {items.map(item => (
//           <div
//             key={item}
//             className="ql-pill"
//             style={{
//               padding: '6px 12px',
//               background: G.surface2,
//               border: `1px solid ${G.border}`,
//               borderRadius: 8,
//               fontSize: 12,
//               color: G.textSecondary,
//               cursor: 'pointer',
//               display: 'flex', alignItems: 'center', gap: 6,
//             }}
//           >
//             <span className="ql-dot-inner" style={{
//               width: 5, height: 5, borderRadius: '50%',
//               background: G.textMuted, flexShrink: 0,
//             }} />
//             {item}
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }

// /* ─── Main Component ─────────────────────────────────────────────────────── */

// function AdminDashboard() {
//   const navigate = useNavigate();
//   const [totalCount, setTotalCount] = useState('—');
//   const [activeLink, setActiveLink] = useState('');

//   /* inject global CSS once */
//   useEffect(() => {
//     const id = 'admin-panel-styles';
//     if (!document.getElementById(id)) {
//       const style = document.createElement('style');
//       style.id = id;
//       style.textContent = globalCss;
//       document.head.appendChild(style);
//     }
//     return () => {
//       const el = document.getElementById(id);
//       if (el) el.remove();
//     };
//   }, []);

//   /* auth guard */
//   useEffect(() => {
//     const email    = localStorage.getItem('email');
//     const password = localStorage.getItem('password');
//     const remember = localStorage.getItem('remember');
//     if (remember === 'false' || !email || !password) navigate('/signin');
//   }, [navigate]);

//   /* fetch total count */
//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const snapshot = await getDocs(collection(db, 'personal_details'));
//         setTotalCount(snapshot.size.toLocaleString('en-IN'));
//       } catch (err) {
//         console.error('Error fetching data:', err);
//       }
//     };
//     fetchData();
//   }, []);

//   const logout = () => {
//     localStorage.removeItem('email');
//     localStorage.removeItem('password');
//     localStorage.removeItem('remember');
//     navigate('/signin');
//   };

//   return (
//     <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: G.bgDark }}>

//       {/* ── SIDEBAR ── */}
//       <aside style={{
//         width: 260, minWidth: 260,
//         background: G.sidebarBg,
//         borderRight: `1px solid ${G.sidebarBorder}`,
//         display: 'flex', flexDirection: 'column', overflow: 'hidden',
//       }}>

//         {/* Brand */}
//         <div style={{
//           padding: '20px 20px 16px',
//           display: 'flex', alignItems: 'center', gap: 12,
//           borderBottom: `1px solid ${G.border}`, flexShrink: 0,
//         }}>
//           <div style={{
//             width: 36, height: 36,
//             background: 'linear-gradient(135deg,#6c63ff,#a78bfa)',
//             borderRadius: 10,
//             display: 'flex', alignItems: 'center', justifyContent: 'center',
//             fontSize: 16, flexShrink: 0,
//           }}>
//             ⚡
//           </div>
//           <div>
//             <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, fontWeight: 700 }}>
//               AdminDashboard
//             </div>
//             <div style={{ fontSize: 11, color: G.textSecondary }}>Management Console</div>
//           </div>
//         </div>

//         {/* Nav scroll */}
//         <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '10px 0 16px' }}>
//           {NAV_SECTIONS.map(sec => (
//             <NavSection
//               key={sec.id}
//               section={sec}
//               activeLink={activeLink}
//               setActiveLink={setActiveLink}
//             />
//           ))}

//           {/* Logout */}
//           <div style={{ padding: '12px 16px 0' }}>
//             <button
//               className="logout-btn"
//               onClick={logout}
//               style={{
//                 width: '100%', padding: '10px 16px',
//                 display: 'flex', alignItems: 'center', gap: 10,
//                 background: 'rgba(239,68,68,.08)',
//                 border: '1px solid rgba(239,68,68,.18)',
//                 borderRadius: 10,
//                 color: G.danger,
//                 fontSize: 13, fontWeight: 500,
//                 cursor: 'pointer',
//               }}
//             >
//               <span>⎋</span> Logout
//             </button>
//           </div>
//         </nav>

//         {/* Footer user row */}
//         <div style={{ padding: '14px 16px', borderTop: `1px solid ${G.border}`, flexShrink: 0 }}>
//           <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
//             <div style={{
//               width: 32, height: 32,
//               background: 'linear-gradient(135deg,#6c63ff,#00d4aa)',
//               borderRadius: 9,
//               display: 'flex', alignItems: 'center', justifyContent: 'center',
//               fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0,
//             }}>A</div>
//             <div>
//               <div style={{ fontSize: 13, fontWeight: 600 }}>Admin</div>
//               <div style={{ fontSize: 11, color: G.textMuted }}>Super Admin</div>
//             </div>
//             <div style={{
//               marginLeft: 'auto',
//               width: 7, height: 7, borderRadius: '50%',
//               background: G.success,
//               boxShadow: `0 0 6px ${G.success}`,
//             }} />
//           </div>
//         </div>
//       </aside>

//       {/* ── MAIN ── */}
//       <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

//         {/* Topbar */}
//         <header style={{
//           height: 60,
//           background: G.surface,
//           borderBottom: `1px solid ${G.border}`,
//           display: 'flex', alignItems: 'center',
//           padding: '0 28px', gap: 16, flexShrink: 0,
//         }}>
//           <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 16, fontWeight: 600, flex: 1 }}>
//             Dashboard Overview
//           </div>
//           <div style={{
//             background: G.accentGlow, color: G.accent,
//             padding: '4px 12px', borderRadius: 20,
//             fontSize: 12, fontWeight: 500,
//             border: '1px solid rgba(108,99,255,.25)',
//           }}>
//             ● Live
//           </div>
//           <div style={{ fontSize: 12, color: G.textSecondary }}>
//             {new Date().toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
//           </div>
//           <div style={{
//             width: 34, height: 34,
//             background: 'linear-gradient(135deg,#6c63ff,#00d4aa)',
//             borderRadius: 10,
//             display: 'flex', alignItems: 'center', justifyContent: 'center',
//             fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer',
//           }}>A</div>
//         </header>

//         {/* Scrollable content */}
//         <main style={{ flex: 1, overflowY: 'auto', padding: 28 }}>

//           {/* Page heading */}
//           <div style={{ marginBottom: 28 }}>
//             <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 24, fontWeight: 700 }}>
//               Welcome back, Admin 👋
//             </h1>
//             <p style={{ color: G.textSecondary, fontSize: 13.5, marginTop: 4 }}>
//               Here's what's happening with your platform today.
//             </p>
//           </div>

//           {/* Stat cards */}
//           <div style={{
//             display: 'grid',
//             gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
//             gap: 16, marginBottom: 28,
//           }}>
//             <StatCard icon="📱" value={totalCount}  label="App Downloads"          change="12% from last month" changeUp delay={1} colorClass="purple" />
//             <StatCard icon="🎓" value="148"          label="Workshop Registrations"  change="8% this week"       changeUp delay={2} colorClass="teal"   />
//             <StatCard icon="📋" value="34"           label="Pending Requests"        change="5 new today"        changeUp delay={3} colorClass="amber"  />
//             <StatCard icon="🏆" value="209"          label="Certificates Issued"     change="3% this month"      changeUp delay={4} colorClass="sky"    />
//           </div>

//           {/* Quick section cards */}
//           <div style={{
//             display: 'grid', gridTemplateColumns: '1fr 1fr',
//             gap: 20, marginBottom: 28,
//           }}>
//             <QuickSection title="📊 App Data"   badge={`${NAV_SECTIONS[0].items.length} sections`}
//               items={NAV_SECTIONS[0].items.slice(0,8).map(i => i.label)} />
//             <QuickSection title="📨 Requests"   badge={`${NAV_SECTIONS[1].items.length} sections`}
//               items={NAV_SECTIONS[1].items.slice(0,8).map(i => i.label)} />
//             <QuickSection title="✏️ Create"     badge={`${NAV_SECTIONS[2].items.length} sections`}
//               items={NAV_SECTIONS[2].items.slice(0,8).map(i => i.label)} />
//             <QuickSection title="🆕 New / Grow" badge={`${NAV_SECTIONS[3].items.length + NAV_SECTIONS[4].items.length} sections`}
//               items={[...NAV_SECTIONS[3].items, ...NAV_SECTIONS[4].items].slice(0,8).map(i => i.label)} />
//           </div>

//           {/* Recent requests table */}
//           <div style={{
//             background: G.surface, border: `1px solid ${G.border}`,
//             borderRadius: 14, overflow: 'hidden',
//           }}>
//             <div style={{
//               padding: '16px 20px',
//               borderBottom: `1px solid ${G.border}`,
//               display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//             }}>
//               <div style={{ fontSize: 14, fontWeight: 600 }}>Recent Requests</div>
//               <div style={{ fontSize: 12, color: G.accent, cursor: 'pointer' }}>View all →</div>
//             </div>
//             <table style={{ width: '100%', borderCollapse: 'collapse' }}>
//               <thead>
//                 <tr style={{ background: G.surface2, borderBottom: `1px solid ${G.border}` }}>
//                   {['User', 'Request Type', 'Date', 'Status'].map(h => (
//                     <th key={h} style={{
//                       padding: '10px 20px', textAlign: 'left',
//                       fontSize: 11, fontWeight: 600, letterSpacing: '.07em',
//                       textTransform: 'uppercase', color: G.textMuted,
//                     }}>{h}</th>
//                   ))}
//                 </tr>
//               </thead>
//               <tbody>
//                 {RECENT_REQUESTS.map((row, i) => {
//                   const bc = BADGE_COLORS[row.statusClass];
//                   return (
//                     <tr
//                       key={i}
//                       className="tbl-row"
//                       style={{ borderBottom: i < RECENT_REQUESTS.length - 1 ? `1px solid ${G.border}` : 'none' }}
//                     >
//                       <td style={{ padding: '12px 20px', fontSize: 13, color: G.textPrimary, fontWeight: 500 }}>{row.user}</td>
//                       <td style={{ padding: '12px 20px', fontSize: 13, color: G.textSecondary }}>{row.type}</td>
//                       <td style={{ padding: '12px 20px', fontSize: 13, color: G.textSecondary }}>{row.date}</td>
//                       <td style={{ padding: '12px 20px' }}>
//                         <span style={{
//                           display: 'inline-block', padding: '3px 9px', borderRadius: 6,
//                           fontSize: 11, fontWeight: 600,
//                           background: bc.bg, color: bc.color,
//                         }}>{row.status}</span>
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>

//           {/* bottom spacer */}
//           <div style={{ height: 24 }} />
//         </main>
//       </div>
//     </div>
//   );
// }

// export default AdminDashboard;
